// ========================================
// pages/TrusteeVerification.js - Trustee responds about user
// ========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const TrusteeVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    fetchVerificationData();
  }, [token]);

  const fetchVerificationData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/verification/trustee/${token}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired verification link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedResponse) {
      alert('Please select Alive or Deceased');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/verification/respond`, {
        token,
        response: selectedResponse
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.errorTitle}>Verification Error</h2>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate('/')} style={styles.button}>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✅</div>
          <h2 style={styles.successTitle}>Thank You</h2>
          <p style={styles.successText}>
            Your response has been recorded. You will be notified if further action is needed.
          </p>
          <button onClick={() => navigate('/')} style={styles.button}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Death Verification</h2>
        
        <div style={styles.infoBox}>
          <p style={styles.userName}>{data?.userName}</p>
          <p style={styles.userNote}>has not checked in for over 90 days.</p>
          <p style={styles.userNote}>As a trusted trustee, please verify their status.</p>
        </div>

        <div style={styles.options}>
          <button
            onClick={() => setSelectedResponse('alive')}
            style={{
              ...styles.optionButton,
              ...(selectedResponse === 'alive' ? styles.selectedAlive : styles.aliveButton)
            }}
          >
            ✓ They are ALIVE
          </button>
          
          <button
            onClick={() => setSelectedResponse('deceased')}
            style={{
              ...styles.optionButton,
              ...(selectedResponse === 'deceased' ? styles.selectedDeceased : styles.deceasedButton)
            }}
          >
            ⚰️ They are DECEASED
          </button>
        </div>

        <button
          onClick={handleSubmitResponse}
          disabled={submitting || !selectedResponse}
          style={{
            ...styles.submitButton,
            ...((submitting || !selectedResponse) ? styles.disabledButton : {})
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Response'}
        </button>

        <p style={styles.note}>
          You can change your response later by clicking the link in your email again.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F0E6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '10px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  title: {
    fontSize: '24px',
    color: '#1A1A1A',
    marginBottom: '20px'
  },
  infoBox: {
    backgroundColor: '#F5F0E6',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '30px'
  },
  userName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#FFD700',
    marginBottom: '10px'
  },
  userNote: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '5px'
  },
  options: {
    display: 'flex',
    gap: '15px',
    marginBottom: '30px'
  },
  optionButton: {
    flex: '1',
    padding: '15px',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  aliveButton: {
    backgroundColor: 'transparent',
    border: '2px solid #2E7D32',
    color: '#2E7D32'
  },
  deceasedButton: {
    backgroundColor: 'transparent',
    border: '2px solid #1A1A1A',
    color: '#1A1A1A'
  },
  selectedAlive: {
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none'
  },
  selectedDeceased: {
    backgroundColor: '#1A1A1A',
    color: 'white',
    border: 'none'
  },
  submitButton: {
    width: '100%',
    padding: '15px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '15px'
  },
  disabledButton: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  note: {
    fontSize: '12px',
    color: '#666',
    marginTop: '15px'
  },
  errorTitle: {
    fontSize: '20px',
    color: '#dc2626',
    marginBottom: '15px'
  },
  errorText: {
    color: '#666',
    marginBottom: '20px'
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '20px'
  },
  successTitle: {
    fontSize: '24px',
    color: '#2E7D32',
    marginBottom: '15px'
  },
  successText: {
    color: '#666',
    marginBottom: '30px',
    lineHeight: '1.6'
  },
  button: {
    padding: '12px 30px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  }
};

export default TrusteeVerification;